import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const InflationCalculator = () => {
  const [currentCost, setCurrentCost] = useState(1000);
  const [inflationRate, setInflationRate] = useState(5); // Inflation rate (per annum)
  const [timePeriod, setTimePeriod] = useState(10); // Time period in years
  const [costIncrease, setCostIncrease] = useState(0);
  const [futureCost, setFutureCost] = useState(0);

  const calculateInflation = () => {
    const futureCostValue = currentCost * Math.pow((1 + inflationRate / 100), timePeriod);
    const costIncreaseValue = futureCostValue - currentCost;

    setCostIncrease(costIncreaseValue.toFixed(2));
    setFutureCost(futureCostValue.toFixed(2));
  };

  const handleCurrentCostChange = (e) => {
    setCurrentCost(parseFloat(e.target.value));
  };

  const handleInflationRateChange = (e) => {
    setInflationRate(parseFloat(e.target.value));
  };

  const handleTimePeriodChange = (e) => {
    setTimePeriod(parseInt(e.target.value));
  };

  const data = {
    labels: ['Current Cost', 'Cost Increase'],
    datasets: [
      {
        label: 'Inflation Breakdown',
        data: [currentCost, costIncrease],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };
function Icon({ id, open }) {
      return (
          <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
          >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
      );
    }
    
    
      const [activeIndex, setActiveIndex] = useState(0);
    
       
      const faqs = [
        {
          question: 'What is inflation in simple terms?',
          answer: 'Inflation is the rate at which the general price level of goods and services increases, reducing your purchasing power over time.',
        },
        {
          question: 'Why is inflation important to understand?',
          answer: 'Because it affects everything from the cost of groceries to your retirement savings. Ignoring inflation can lead to underestimating future expenses.',
        },
        {
          question: 'How does inflation affect savings?',
          answer: 'It reduces the real value of your savings if the interest earned is lower than the inflation rate.',
        },
        {
          question: 'What causes inflation?',
          answer: 'Common causes include increased demand, rising production costs, and expansionary monetary policies.',
        },
        {
          question: 'What is the current inflation rate in India?',
          answer: 'It varies monthly and can be found on official government or financial websites.',
        },
        {
          question: 'What is the difference between CPI and WPI?',
          answer: 'CPI measures retail prices for consumers, while WPI tracks prices at the wholesale level.',
        },
        {
          question: 'Is inflation good or bad?',
          answer: 'Moderate inflation is healthy for the economy, but high or volatile inflation can be damaging.',
        },
        {
          question: 'How do inflation calculators work?',
          answer: 'They use past inflation rates to project the future value of money or estimate the loss in purchasing power.',
        },
        {
          question: 'Are inflation calculators accurate?',
          answer: 'They provide good estimates based on historical data but may not account for sudden economic changes.',
        },
        {
          question: 'What data do I need to use an inflation calculator?',
          answer: 'Typically, you’ll need the amount of money, the number of years, and the average inflation rate.',
        },
        {
          question: 'Can inflation calculators help with retirement planning?',
          answer: 'Yes, they can show how much money you’ll need to save to maintain your lifestyle in the future.',
        },
        {
          question: 'How can I beat inflation with investments?',
          answer: 'Invest in instruments like stocks, mutual funds, or inflation-indexed bonds that have the potential to offer higher returns.',
        },
        {
          question: 'Is it safe to rely only on fixed deposits during high inflation?',
          answer: 'Not always. Fixed deposits may offer returns lower than the inflation rate, leading to a loss in purchasing power.',
        },
        {
          question: 'How often should I calculate the impact of inflation?',
          answer: 'Review your calculations annually or whenever you revise your financial goals.',
        },
        {
          question: 'Where can I access a reliable inflation calculator?',
          answer: 'You can use TheFinMaster’s free online inflation calculator for accurate and easy-to-use projections.',
        },
      ];
      const toggleAccordion = (index) => {
        if (activeIndex === index) {
            // If the clicked question is already expanded, collapse it
            setActiveIndex(null);
        } else {
            // If the clicked question is not expanded, expand it
            setActiveIndex(index);
        };
      }
  return (
    <>
     <Helmet>
         <title>Inflation Calculator – Real Value of Money Over Time</title>
        <meta  name='description' content='Estimate how inflation affects the value of money over time with our inflation calculator.'/>
        <meta name='keywords' content='inflation rate calculator, swp calculator with inflation, lumpsum calculator with inflation, adjusted inflation rate calculator, calculator to adjust for inflation, time and value of money calculator' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/inflation-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Inflation Calculator</span>
        </li> 
      
      </ol>
    </nav>
    <div className='py-4'>
      <div className="container mx-auto p-4 shadow-xl rounded-md ">
        <h1 className="text-2xl font-bold mb-4">Inflation Calculator</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Current Cost</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  value={currentCost}
                  onChange={handleCurrentCostChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {currentCost.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="100000"
                value={currentCost}
                onChange={(e) => setCurrentCost(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Rate of Inflation (P.A.)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={inflationRate}
                  onChange={handleInflationRateChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">{inflationRate} %</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={inflationRate}
                onChange={(e) => setInflationRate(parseFloat(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Time Period (Years)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={timePeriod}
                  onChange={handleTimePeriodChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">{timePeriod} Yr</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                value={timePeriod}
                onChange={(e) => setTimePeriod(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <button
              onClick={calculateInflation}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Calculate
            </button>
          </div>
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-bold">Results</h2>
              <p>Current Cost: ₹ {parseFloat(currentCost).toLocaleString()}</p>
              <p>Cost Increase: ₹ {parseFloat(costIncrease).toLocaleString()}</p>
              <p>Future Cost: ₹ {parseFloat(futureCost).toLocaleString()}</p>
            </div>
            <div>
              <h2 className="text-xl font-bold">Breakdown</h2>
              <div className="flex justify-center">
                <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    
    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is Inflation?
          </h1>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Inflation refers to the general increase in the prices of goods and services in an economy over a period of time. It erodes the purchasing power of money, meaning that a given amount of money will buy fewer goods and services than before. This phenomenon is observed over time and is a natural outcome of growing economies. For example, if you were able to buy a basket of groceries for Rs. 1,000 last year and today the same basket costs Rs. 1,100, the inflation rate is effectively 10%.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Inflation is not necessarily a negative indicator. Moderate inflation is considered a sign of a healthy economy. However, when inflation is too high or erratic, it can severely impact personal finances, especially for those with fixed incomes or cash savings.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            To measure inflation, economists rely on two main indexes:
          </p>
          <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            <li><strong>Consumer Price Index (CPI):</strong> This index tracks the prices of a set basket of consumer goods and services like food, housing, clothing, and transportation. It is most relevant to everyday consumers.</li>
            <li><strong>Wholesale Price Index (WPI):</strong> This measures price changes at the wholesale level—before the goods reach the consumer. It reflects how much more it costs businesses to procure items.</li>
          </ul>
</div>
          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Inflation Affects Your Savings
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The impact of inflation on your savings can be significant. While you may be adding money to your savings account regularly, the real value—or the purchasing power—of that money could be decreasing over time. This is because the interest you earn on savings or fixed deposits may not always keep pace with the inflation rate.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Let’s consider an example: If your savings account offers an annual interest rate of 4%, and the inflation rate is 6%, your real return is -2%. Even though your money appears to be growing in absolute terms, it is losing value in practical terms.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To safeguard your wealth, it's important to invest in financial instruments that have the potential to offer returns higher than the inflation rate. These can include:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Stocks and equity mutual funds</li>
              <li>Real estate investments</li>
              <li>Commodities like gold</li>
              <li>Inflation-indexed bonds</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Prepare to Overcome Inflation
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Inflation may be inevitable, but its effects can be mitigated through smart financial planning. Here’s how you can proactively protect your purchasing power:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Invest Strategically:</strong> Rather than parking all your money in a low-yield savings account, diversify your investments. Consider a balanced portfolio including equity, debt, gold, and real estate. Equity, in particular, has historically provided returns that beat inflation over the long term.</li>
              <li><strong>Review Financial Goals Regularly:</strong> Your future financial goals, such as retirement, education, or buying a home, need to be adjusted for inflation. Review and revise them periodically to ensure that your savings and investments are aligned with real-world costs.</li>
              <li><strong>Use SIPs and Lump Sum Investments:</strong> Systematic Investment Plans (SIPs) in mutual funds allow for disciplined investing and can compound returns over time. Lump sum investments in growth-oriented assets can also be beneficial if timed well.</li>
              <li><strong>Diversify to Manage Risk:</strong> Different asset classes react differently to economic cycles. By diversifying, you reduce the risk of your entire portfolio being adversely affected by inflation.</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is an Inflation Calculator?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              An Inflation Calculator is a digital tool designed to help users understand the impact of inflation on the value of money over time. It estimates how much a certain sum of money today would be worth in the future, or vice versa, based on the average rate of inflation.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This tool is especially useful for financial planning, retirement planning, and investment comparisons. Here are the main features and uses of an inflation calculator:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Purchasing Power Estimation:</strong> It helps you determine how much your current savings will be worth in the future.</li>
              <li><strong>Value Projection:</strong> It allows you to input an amount and project its value 5, 10, or 20 years from now.</li>
              <li><strong>Investment Evaluation:</strong> Compare the effect of inflation on money kept idle versus money invested.</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              If Rs. 1,00,000 is kept idle for 10 years and the average inflation rate is 5%, the future value in today’s terms might be just Rs. 61,000. This shows how essential it is to earn returns that beat inflation.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is Inflation Calculated?
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Inflation is typically measured using the Consumer Price Index (CPI). The CPI tracks changes in the price of a fixed basket of consumer goods and services.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>CPI Calculation:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              CPI = (Cost of basket in current year / Cost of basket in base year) x 100
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The percentage change in CPI over a time period is the inflation rate.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Inflation Rate Formula:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Inflation (%) = ((CPI in year X+1 – CPI in year X) / CPI in year X) x 100
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This formula helps economists, policymakers, and individuals gauge the rate at which prices are rising and plan accordingly.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Benefits of Using an Inflation Calculator
            </h1>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Inflation calculators are not just for economists or finance professionals. They are incredibly helpful tools for everyday financial planning. Here’s how they benefit users:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Future Value Prediction:</strong> Understand what your current savings will be worth in the future, so you can plan accordingly.</li>
              <li><strong>Budget Planning:</strong> Adjust your savings goals based on expected inflation to meet your future expenses like education, travel, or retirement.</li>
              <li><strong>Compare Investment Returns:</strong> Evaluate whether an investment’s return is sufficient to beat inflation and preserve your purchasing power.</li>
              <li><strong>Saves Time and Effort:</strong> No need for manual calculations or financial formulas—get quick and accurate results.</li>
              <li><strong>Financial Awareness:</strong> Improves understanding of how inflation impacts your personal finances and enhances decision-making.</li>
            </ul>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h1>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

    </>
  );
};

export default InflationCalculator;
